'use client';

/**
 * OntologyProvider — build-time ontology import + typed React-context accessors.
 *
 * Implements Architecture Decision 4.11: the case ontology is bundled at
 * build time via a static JSON import, validated by Zod at module load
 * (intentional throw-on-drift per FR-S3), frozen, and exposed through a
 * client-side React Context.
 *
 * Primitive components (EntityCard, PoleTabBar, SpeechActCard, ...) consume
 * the ontology exclusively through the typed accessors declared here —
 * never via string literals — which underwrites FR-S2 (ontology-driven
 * labels) and is enforced at CI by the ontology-invariant test that lands
 * in edv-1.7.
 *
 * `useEdgeTypeMeta` returns the raw `EdgeType` (Zod-inferred from
 * `EdgeTypeSchema` in `types/evidence/ontology.types.ts`). The
 * `evidentiary: boolean` field on that type was added in `edv-5.7` and
 * classifies each edge type for the FR-C19 inline-quote rendering gate
 * in `RelationshipRow.tsx`; see `DECISIONS.md` 2026-05-14 for the
 * 23-edge-type classification rationale.
 */

import { createContext, useContext, type ReactNode } from 'react';

import ontologyJson from '../../../../../shared/ontology/case_ontology.json';

import {
  OntologySchema,
  type EdgeType,
  type Ontology,
  type PoleCategory,
} from '@/types/evidence/ontology.types';
import { poleCategoryFor } from '@/lib/evidence/pole-categorization';

/**
 * Recursively freezes an object and every nested object/array. AC3 promises
 * "prevent accidental mutation in dev"; the bare `Object.freeze` only seals
 * the top-level keys, so a consumer could still write
 * `useOntology().node_types[0].label = 'pwned'` without error. deepFreeze
 * walks the whole tree so the guarantee holds everywhere a primitive reads.
 *
 * Idempotent — re-freezing an already-frozen branch is skipped via
 * `Object.isFrozen` to avoid the (small) cost of repeated traversal.
 */
function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== 'object' || Object.isFrozen(value)) {
    return value;
  }
  Object.freeze(value);
  for (const key of Object.keys(value as object)) {
    deepFreeze((value as Record<string, unknown>)[key]);
  }
  return value;
}

const ontologyParsed: Ontology = deepFreeze(OntologySchema.parse(ontologyJson));

// Module-load membership index for `useFallbackTypeLabel`. O(1) lookup avoids
// scanning `node_types` on every primitive render.
const KNOWN_NODE_LABELS: ReadonlySet<string> = new Set(
  ontologyParsed.node_types.map((node) => node.label)
);

const OntologyContext = createContext<Ontology | null>(null);

export function OntologyProvider({ children }: { children: ReactNode }) {
  return (
    <OntologyContext.Provider value={ontologyParsed}>
      {children}
    </OntologyContext.Provider>
  );
}

export function useOntology(): Ontology {
  const ctx = useContext(OntologyContext);
  if (!ctx) {
    throw new Error('useOntology must be used inside <OntologyProvider>');
  }
  return ctx;
}

export function usePoleCategoryFor(type: string): PoleCategory {
  // Load-bearing: enforces the provider-boundary contract per AC4 so this
  // hook throws in the same misuse cases as the other three accessors.
  // Without it, callers could invoke `usePoleCategoryFor` outside an
  // `<OntologyProvider>` (or in a Server Component) and silently get a
  // result, only to break later when refactored to read ontology data.
  // Do NOT remove. Returned ontology is intentionally unused — the POLE
  // map is a presentation-layer derivation that does not consult the
  // ontology JSON (see `lib/evidence/pole-categorization.ts`).
  void useOntology();
  return poleCategoryFor(type);
}

export function useEdgeTypeMeta(type: string): EdgeType | null {
  const ontology = useOntology();
  return (
    ontology.edge_types.find((edge) => edge.relationship_type === type) ?? null
  );
}

export function useFallbackTypeLabel(unknownType: string): string {
  // Load-bearing: enforces the provider-boundary contract per AC4. Do NOT
  // remove — the membership check below uses the module-load
  // `KNOWN_NODE_LABELS` index for O(1) lookup, so the returned ontology
  // is intentionally unused. Without this call there is no provider-
  // boundary guarantee for `useFallbackTypeLabel` (covered by a regression
  // test in `ontology-context.test.tsx`).
  void useOntology();
  if (!KNOWN_NODE_LABELS.has(unknownType)) {
    return 'Unknown type';
  }
  return humanizeLabel(unknownType);
}

/**
 * Strips ontology label prefixes (`nc:`, `j:`) and inserts spaces around
 * camel/PascalCase boundaries — including acronym boundaries — so labels
 * render as space-separated words.
 *
 * Examples:
 *   'nc:Person'      → 'Person'
 *   'j:Evidence'     → 'Evidence'
 *   'PhoneCall'      → 'Phone Call'
 *   'MentionedEvent' → 'Mentioned Event'
 *   'IRSAgent'       → 'IRS Agent'        (acronym → word)
 *   'XMLDocument'    → 'XML Document'     (acronym → word)
 *
 * Two-pass regex:
 *   1. Insert space between an acronym run and a following Capitalized word
 *      (`IRS|Agent`).
 *   2. Insert space between a lowercase letter and an uppercase letter
 *      (`Phone|Call`).
 */
function humanizeLabel(label: string): string {
  return label
    .replace(/^(nc|j):/, '')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z])([A-Z])/g, '$1 $2');
}
